import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '@rind/auth';
import { ThemeService } from '@rind/ui';
import { InventoryPermissions } from '../core/api/models';
import { TenantDirectoryService } from '../core/tenancy/tenant-directory';
import { TenantNamePipe } from '../core/tenancy/tenant-name.pipe';

type NavSection = 'operations' | 'reporting' | 'configuration' | 'products' | 'trade-partners';

const NAV_COLLAPSE_STORAGE_KEY = 'inventory.web.nav.collapsed';
const SIDEBAR_COLLAPSE_STORAGE_KEY = 'inventory.web.sidebar.collapsed';

const SECTION_PATHS: Record<NavSection, string[]> = {
  operations: ['/skus', '/deliveries'],
  reporting: ['/reports'],
  configuration: ['/products', '/stations', '/trade-partners', '/people', '/operators'],
  products: ['/products'],
  'trade-partners': ['/trade-partners'],
};

const NAV_SECTIONS: readonly NavSection[] = [
  'operations',
  'reporting',
  'configuration',
  'products',
  'trade-partners',
];

@Component({
  selector: 'app-shell',
  imports: [RouterOutlet, RouterLink, RouterLinkActive, TenantNamePipe],
  templateUrl: './shell.html',
  styleUrl: './shell.scss',
})
export class Shell implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly theme = inject(ThemeService);
  /** Ensures session location codes seed {@link TenantNamePipe} labels. */
  private readonly tenants = inject(TenantDirectoryService);
  readonly auth = inject(AuthService);

  readonly apiStatus = signal<'checking' | 'ok' | 'down'>('checking');
  readonly isDark = this.theme.isDark;
  readonly currentUrl = signal(this.router.url);
  readonly showProducts = computed(() => this.auth.can(InventoryPermissions.ProductsRead));
  readonly showContainers = computed(() =>
    this.auth.can(InventoryPermissions.ContainersRead),
  );
  readonly showProductsGroup = computed(
    () => this.showProducts() || this.showContainers(),
  );
  readonly showStations = computed(
    () =>
      this.auth.can(InventoryPermissions.DevicesRead) &&
      this.auth.can(InventoryPermissions.ProductContainersRead),
  );
  readonly showSkus = computed(() => this.auth.can(InventoryPermissions.SkusRead));
  readonly showDeliveries = computed(() =>
    this.auth.can(InventoryPermissions.DeliveriesRead),
  );
  readonly showReports = computed(() => this.showSkus());
  readonly showTradePartners = computed(() =>
    this.auth.can(InventoryPermissions.TradePartnersRead),
  );
  readonly showOperators = computed(() =>
    this.auth.can(InventoryPermissions.OperatorsRead),
  );
  readonly showOperations = computed(() => this.showSkus() || this.showDeliveries());
  readonly showConfiguration = computed(
    () =>
      this.showProductsGroup() ||
      this.showStations() ||
      this.showTradePartners() ||
      this.showOperators(),
  );
  readonly email = computed(() => this.auth.email());
  readonly tenantId = computed(() => this.auth.tenantId());
  readonly locationCode = computed(() => this.auth.locationCode());
  readonly hasAnyFeature = computed(
    () => this.showOperations() || this.showConfiguration() || this.showReports(),
  );

  private readonly collapsed = signal<ReadonlySet<NavSection>>(loadCollapsedSections());
  readonly sidebarCollapsed = signal(loadSidebarCollapsed());

  ngOnInit(): void {
    this.http.get('/health', { responseType: 'text' }).subscribe({
      next: () => this.apiStatus.set('ok'),
      error: () => this.apiStatus.set('down'),
    });

    this.expandSectionForUrl(this.router.url);
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
        this.expandSectionForUrl(event.urlAfterRedirects);
      });
  }

  isExpanded(section: NavSection): boolean {
    return !this.collapsed().has(section);
  }

  isSectionActive(section: NavSection): boolean {
    const url = this.currentUrl();
    return SECTION_PATHS[section].some(
      (path) => url === path || url.startsWith(`${path}/`),
    );
  }

  toggleSection(section: NavSection): void {
    const next = new Set(this.collapsed());
    if (next.has(section)) {
      next.delete(section);
    } else {
      next.add(section);
    }
    this.collapsed.set(next);
    persistCollapsedSections(next);
  }

  toggleSidebar(): void {
    const next = !this.sidebarCollapsed();
    this.sidebarCollapsed.set(next);
    persistSidebarCollapsed(next);
  }

  toggleTheme(): void {
    this.theme.toggle();
  }

  logout(): void {
    this.auth.logout();
  }

  private expandSectionForUrl(url: string): void {
    const path = url.split('?')[0] ?? url;
    const next = new Set(this.collapsed());
    let changed = false;
    for (const section of NAV_SECTIONS) {
      const prefixes = SECTION_PATHS[section];
      if (prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
        if (next.has(section)) {
          next.delete(section);
          changed = true;
        }
      }
    }
    if (changed) {
      this.collapsed.set(next);
      persistCollapsedSections(next);
    }
  }
}

function loadCollapsedSections(): ReadonlySet<NavSection> {
  try {
    const raw = localStorage.getItem(NAV_COLLAPSE_STORAGE_KEY);
    if (!raw) {
      // Reporting starts collapsed — nothing to show yet.
      return new Set<NavSection>(['reporting']);
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return new Set<NavSection>(['reporting']);
    }
    return new Set(
      parsed.filter((value): value is NavSection =>
        NAV_SECTIONS.includes(value as NavSection),
      ),
    );
  } catch {
    return new Set<NavSection>(['reporting']);
  }
}

function persistCollapsedSections(collapsed: ReadonlySet<NavSection>): void {
  localStorage.setItem(NAV_COLLAPSE_STORAGE_KEY, JSON.stringify([...collapsed]));
}

function loadSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function persistSidebarCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSE_STORAGE_KEY, collapsed ? '1' : '0');
  } catch {
    // Private mode / storage blocked — preference still applies for this session.
  }
}
